// Combobox — text input with a dropdown list of suggestions.
//
// Used for party slug + treasury slug autocompletes. Operator can still type
// any value (new slug) — the dropdown is a helper, not a constraint.
//
// NAMING + RELEVANCE (2026-08-12 operator pass): rows render through the one
// presentation component (<Name/>), so a combobox row looks identical to a
// RailPicker row, a Parties row and a palette row — primary label, muted
// secondary line, right-aligned trailing figure, never concatenated.
//
// Callers own the option payload, so they build it with the shared helpers
// rather than with local predicates:
//
//   options={parties.map((p) => ({
//     value:     p.slug,
//     label:     partyName(p),                      // never p.display_name
//     secondary: partySubtitle(p, { compact: true }),
//     stale:     isStale(p),                        // tier 3 → behind Show all
//     review:    needsNameReview(p.display_name),
//   }))}
//
// `stale` is the only relevance input this component needs: pickers hide
// exactly tier 3, and an option with no `stale` flag is always shown, so
// call sites that have not migrated behave exactly as before.

import { useState, useRef, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Name } from './Name';
import { cutRosterWords } from '../lib/roster-cut';

/** Mirrors `needsNameReview()`'s return type structurally, so this component
 *  does not depend on a type alias being re-exported by Name.tsx. */
export type ComboboxReview = 'profanity' | 'ambiguous' | 'empty' | null;

export interface ComboboxOption {
  value: string;            // the actual slug typed into the input
  label?: string;           // human label; defaults to value
  /** @deprecated legacy right-hand text. Prefer `trailing` (figure) or
   *  `secondary` (kind/standing/state). Never pass a raw enum here. */
  hint?: string;
  secondary?: string;       // muted second line (e.g. partySubtitle())
  trailing?: ReactNode;     // right-aligned figure (asset chip + balance)
  group?: string;           // optional group header
  stale?: boolean;          // relevance tier 3 — parked behind "Show all (N)"
  review?: ComboboxReview;  // needsNameReview() result, surfaced as a flag
}

/** THE ONE EMPTY LINE for a party picker (SWEEP-SCOPE §6 "Party picker",
 *  item 77). Two variants, decided once (14 Sept 2026): a picker that only
 *  finds parties says the first; a picker beside a create-as panel — Book
 *  trade, both cheque pickers — says the second, because there the next move
 *  is to keep typing, not to leave the screen. No screen writes its own. */
export const NO_PARTY_HINT = 'No party by that name — check the spelling, or create them first.';
export const NO_PARTY_CREATE_HINT = 'No party by that name — type a new name to create them.';

export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  loading,
  emptyHint = NO_PARTY_HINT,
  readFailed,
  more,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  loading?: boolean;
  /** The one line for a search that found nobody. `null` says nothing: the picker has not searched yet (its box is empty and it asks the
   *  books only for typed text), so there is no "none found" — and with nothing else to show, no list is drawn. */
  emptyHint?: string | null;
  /** THE SEARCH COULD NOT BE READ: a line with its own way out (the screen passes <ReadFailed …/>). Closed, it stands under the
   *  box. Open, it is the FIRST thing inside the list — the list floats over the space under the box (absolute, z-50), so a line
   *  left outside it is covered: its Try again and Details seen through the gap but not pressable (Astra, 22 Oct 2026). And the
   *  empty hint — "No party by that name — type a new name to create them", an invitation to a duplicate — is not said over a
   *  search that failed; rows still in hand from an earlier read stay under the line. A successful empty search is unchanged. */
  readFailed?: ReactNode;
  /** THE LIST WENT PAST ITS PAGE (`more: true` on a party answer — lib/roster-cut.ts): these are the FIRST options, not all of them, and
   *  the text typed here is what reaches the rest (it is sent to the books). Said last in the open list, so a list that ends is not read
   *  as the whole roster. Only a picker whose text IS sent passes it. */
  more?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // "Show all" is a per-visit escape hatch, not a preference.
  useEffect(() => { if (!open) setShowAll(false); }, [open]);

  const q = value.toLowerCase().trim();

  /**
   * Separators are not information here. A slug spells a space as `-`, a label
   * may spell it as nothing at all, and the operator types the name the way it
   * reads in legacy. Folding every run of non-alphanumerics to one space on BOTH
   * sides is what makes those three agree.
   *
   * 2026-08-24: typing "R Telegram" in the MC customer field returned
   * "No match — type a new name to onboard", which invites onboarding a
   * DUPLICATE of a party that already exists. The server had already returned
   * the row; this filter dropped it. Two things had to line up:
   *   - the slug is `r-telegram`, and "r-telegram".includes("r telegram") is
   *     false on the hyphen alone;
   *   - the label is not "R Telegram" either. naming.ts strips trailing SOURCE
   *     SUFFIXES, and `telegram` is one of them, so partyName() renders "R".
   * Both comparisons failed and the party vanished from its own search.
   *
   * It was never specific to that party. Every name ending in a source suffix
   * behaves the same way — Billa OTC, Loki OTC, Chris TG, Mandosa B2B,
   * Oussama Ref. — which is to say most of the merchant desks, whenever the
   * operator types the name in full.
   */
  const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const qf = fold(q);

  // Filter by the typed text, then split off the stale tail. A query reaches
  // every tier — hiding is a search affordance, so it stops applying the
  // moment the operator is actually searching.
  const { shown, hidden } = useMemo(() => {
    const hits = q
      ? options.filter((o) => {
          const value_ = fold(o.value);
          const label_ = fold(o.label ?? '');
          if (value_.includes(qf) || label_.includes(qf)) return true;
          // Token subset, so word order and extra words the label dropped do
          // not hide a row: "r telegram" still finds a label of "r".
          const hay = `${value_} ${label_}`;
          return qf.split(' ').filter(Boolean).every((t) => hay.includes(t));
        })
      : options;
    if (q) return { shown: hits, hidden: [] as ComboboxOption[] };
    return {
      shown:  hits.filter(o => !o.stale),
      hidden: hits.filter(o => o.stale),
    };
  }, [options, q, qf]);

  // `emptyHint={null}`: the caller asked the books nothing yet (a picker that searches only typed text, its box still empty), so there is
  // no "none found" to say — and with nothing else to show, no list is drawn at all.
  const quiet = emptyHint === null && options.length === 0 && !more && !loading && !readFailed;

  // Re-group shown options (preserving caller order)
  const groups: Record<string, ComboboxOption[]> = {};
  const groupOrder: string[] = [];
  for (const o of shown) {
    const g = o.group ?? '';
    if (!(g in groups)) { groups[g] = []; groupOrder.push(g); }
    groups[g]!.push(o);
  }

  const renderRow = (o: ComboboxOption, key: string, dimmed = false) => (
    <button
      key={key}
      type="button"
      onMouseDown={() => { onChange(o.value); setOpen(false); }}
      className={`w-full text-left px-3 py-1.5 hover:bg-tg-secondary ${dimmed ? 'opacity-70 hover:opacity-100' : ''}`}
    >
      <Name
        primary={o.label ?? o.value}
        secondary={o.secondary}
        trailing={o.trailing ?? o.hint}
        review={o.review}
        wrapSecondary
      />
    </button>
  );

  // ONE NAME EVERYWHERE — THE FIELD INCLUDED (operator, 2 Sept: "why name
  // ambiguity in selection and when it lands": the list said 'Loki OTC', the
  // landed field said 'loki-otc'). Closed box shows the picked option's NAME;
  // open box shows the raw text so search and lazy-create still work. The
  // stored value stays the slug — display is not storage.
  const selectedOpt = options.find((o) => o.value === value);
  const displayText = !open && selectedOpt ? (selectedOpt.label ?? selectedOpt.value) : value;

  return (
    <div ref={ref} className="relative">
      <input
        className="input"
        value={displayText}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setShowAll(false); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
      />
      {!open && readFailed && <div className="mt-1">{readFailed}</div>}
      {open && !quiet && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-tg-section-bg rounded-lg shadow-lg max-h-72 overflow-y-auto border border-tg-secondary">
          {readFailed && <div className="px-3 py-2">{readFailed}</div>}
          {loading && <p className="text-tg-hint text-meta px-3 py-2">…</p>}
          {/* A collapsed stale tail is not an empty result — the footer below
              still has rows behind it, so the "no match" line stays off. Nor is a CUT list a list that found nobody: the first of the
              matches were read, and the line at the foot says so. */}
          {!loading && !readFailed && !more && emptyHint !== null && shown.length === 0 && hidden.length === 0 && (
            <p className="text-tg-hint text-meta px-3 py-2">{emptyHint}</p>
          )}
          {!loading && groupOrder.map(g => (
            <div key={g}>
              {g && <p className="text-tg-hint text-meta px-3 pt-2 pb-1">{g}</p>}
              {groups[g]!.map(o => renderRow(o, `${g}-${o.value}`))}
            </div>
          ))}

          {/* Nothing is ever deleted — the stale tail sits behind a true count. */}
          {!loading && hidden.length > 0 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full border-t border-tg-secondary px-3 py-2 text-left text-meta text-tg-hint hover:bg-tg-secondary"
            >
              Show all ({hidden.length})
            </button>
          )}
          {!loading && hidden.length > 0 && showAll && (
            <div>
              <p className="text-tg-hint text-meta px-3 pt-2 pb-1 border-t border-tg-secondary">
                Quiet accounts
              </p>
              {hidden.map(o => renderRow(o, `stale-${o.group ?? ''}-${o.value}`, true))}
            </div>
          )}
          {!loading && more && (
            <p className="text-tg-hint text-meta px-3 py-2">{cutRosterWords(options.length, 'type')}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Convenience wrapper: turn a flat list of strings into ComboboxOption[]. */
export function plainOptions(values: string[], group?: string): ComboboxOption[] {
  return values.map(v => ({ value: v, group }));
}
